<template>
  <div>
    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Edit Time Based Channel
            </h3>

            <p>
              Edit the details of the time based channel.
            </p>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <TextFormGroup
                    v-model="data.name"
                    name="name"
                    label="Price"
                    help-text="The name of the time based channel."
                    :error="getError('name')"
                />

                <ColorFormGroup
                    v-model="data.color"
                    name="color"
                    label="Color"
                    help-text="The color of the channel."
                    :colors="colors"
                    :error="getError('color')"
                />

                <SelectFormGroup
                    v-model="data.accounting_system_product_number"
                    name="accounting_system_product_number"
                    label="Accounting System Product"
                    help-text="The product in the accounting system that the channel belongs to."
                    :error="getError('accounting_system_product_number')"
                    :options="bookkeepingProductNumberOptions"
                />

                <SelectFormGroup
                    v-model="data.channel_type"
                    name="channel_type"
                    label="Channel Type"
                    help-text="The channel type of the channel."
                    :error="getError('channel_type')"
                    :options="channelTypeOptions"
                    :nullable="true"
                />

                <NumberFormGroup
                    v-model="data.default_content_deadline_period"
                    name="default_content_deadline_period"
                    label="Default Content Deadline"
                    help-text="The default number of days before publication of the ad that the content should be submitted."
                    :error="getError('default_content_deadline_period')"
                />

                <NumberFormGroup
                    v-model="data.default_content_deadline_reminder_period"
                    name="default_content_deadline_reminder_period"
                    label="Default Content Deadline Reminder"
                    help-text="The default number of days before the content deadline of the ad that a reminder should be sent to the content responsible."
                    :error="getError('default_content_deadline_period')"
                />

                <hr>

                <SubmitFormGroup button-class="btn-black"/>
              </form>
            </div>
          </div>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Subchannels
            </h3>

            <p>
              These are the subchannels under this channel.
            </p>

            <a
                href="#create-time-based-subchannel-modal"
                class="btn btn-black btn-sm"
                data-toggle="modal"
            >
              <i class="fa fa-plus"></i> New
            </a>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <TimeBasedSubchannelListPanel :portal="portal" :time-based-channel="timeBasedChannel"/>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Ad Locations
            </h3>

            <p>
              These are the ad locations that are available in this channel.
            </p>

            <a
                href="#create-time-based-ad-location-modal"
                class="btn btn-black btn-sm"
                data-toggle="modal"
            >
              <i class="fa fa-plus"></i> New
            </a>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <TimeBasedAdLocationListPanel :time-based-channel="timeBasedChannel"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import ColorFormGroup from '@/components/FormGroups/Horizontal/Color'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TimeBasedSubchannelListPanel from '@/components/Administration/TimeBasedSubchannels/Panels/List'
import TimeBasedAdLocationListPanel from '@/components/Administration/TimeBasedAdLocations/Panels/List'

export default {
  components: {
    ColorFormGroup,
    HelpText,
    LoadingContainer,
    NumberFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    TextFormGroup,
    TimeBasedSubchannelListPanel,
    TimeBasedAdLocationListPanel
  },

  mixins: [Errors],

  model: {
    prop: 'portal',
    event: 'portal-updated'
  },

  props: ['portal', 'timeBasedChannelId'],

  data() {
    return {
      data: {},

      timeBasedChannel: null,
      bookkeepingProducts: null,
      channelTypes: null,
      colors: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    bookkeepingProductNumberOptions() {
      return this.bookkeepingProducts.map(bookkeepingProduct => {
        return {
          value: bookkeepingProduct.id,
          label: bookkeepingProduct.attributes.name
        }
      })
    },

    channelTypeOptions() {
      return this.channelTypes.map(channelType => {
        return {
          value: channelType.id,
          label: channelType.attributes.name
        }
      })
    }
  },

  methods: {
    load() {
      Promise.all([
        http.get('/api/v2/time-based-channels/' + this.timeBasedChannelId),
        http.get('/api/v2/bookkeeping-products'),
        http.get('/api/v2/colors'),
        http.get('/api/v2/channel-types'),
      ]).then(responses => {
        this.timeBasedChannel = responses[0].data.data

        this.data = {...this.timeBasedChannel.attributes}

        this.bookkeepingProducts = responses[1].data.data

        this.colors = responses[2].data.data

        this.channelTypes = responses[3].data.data

        this.loading = false
      })
    },

    submit() {
      http.put('/api/v2/time-based-channels/' + this.timeBasedChannel.id, this.data)
          .then(() => {
            notify.success('The time based channel was saved')

            this.errors = {}
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
